import { Box, Span } from "@chakra-ui/react";
import { ReactComponent as ArrowRightIcon } from "assets/icons/arrow-right.svg";
import Select, { StylesConfig } from "react-select";

import DateInput from "ui/date-input";
import { radioCircle, radioWrapper } from "ui/export/styles";
import {
  FilterBodyItem,
  filterBodyItemCss,
} from "ui/filter/filter-body/styles";
import {
  IDateRangeValues,
  IFilterFormFieldOption,
  TFilterOption,
  TFilterValue,
  TFilterValues,
} from "ui/filter/types";
import { StyledCheckbox } from "ui/styled-checkbox";
import { Paragraph3, Paragraph4 } from "ui/typography";

import { formatDateToYYYYMMDD } from "./formatters";

export const validateFilterValues = (
  options: TFilterOption[],
  values: TFilterValues
): boolean => {
  return options.every((option) => {
    const value = values[option.key];

    switch (option.type) {
      case "checkbox":
        return (
          Array.isArray(value) && value.every((v) => typeof v === "string")
        );
      case "date-range":
        return (
          typeof value === "object" &&
          "startDate" in value &&
          "endDate" in value
        );
      case "select":
        return typeof value === "string";
      case "radio":
        return typeof value === "string";
      default:
        return false;
    }
  });
};

export const renderCheckboxFilters = (
  filter: TFilterOption,
  values: TFilterValues,
  onChange: (key: string, value: TFilterValue) => void
): React.ReactNode => {
  if (filter.type !== "checkbox") return <></>;
  const { title, options, key } = filter;
  const checkBoxValues = values[key] as string[];

  return (
    <FilterBodyItem css={filterBodyItemCss}>
      {title && (
        <div className="flex items-center h-10">
          <Paragraph4
            fontWeight="600"
            lineHeight="20px"
            textColor="#1A202C"
          >
            {title}
          </Paragraph4>
        </div>
      )}
      <div className="flex-wrap flex gap-4">
        {options.map(({ label, value }, idx) => (
          <div
            className="inline-flex items-center gap-2 h-10 pr-4"
            key={idx}
          >
            <StyledCheckbox
              name={label}
              isChecked={checkBoxValues.includes(value)}
              handleCheckBoxChange={() => {
                const isValueExists = checkBoxValues.find(
                  (checkboxValue) => checkboxValue === value
                );
                let newValues = [];
                if (!isValueExists) {
                  newValues = [...checkBoxValues, value];
                } else {
                  newValues = checkBoxValues.filter(
                    (checkBoxValue) => checkBoxValue !== value
                  );
                }
                onChange(key, newValues);
              }}
              disabled={filter?.isDisabled}
            />
            <Paragraph3
              fontWeight="600"
              lineHeight="20px"
              textColor="#344054"
              className={`${filter?.isDisabled ? "cursor-not-allowed opacity-40" : ""}`}
            >
              {label}
            </Paragraph3>
          </div>
        ))}
      </div>
    </FilterBodyItem>
  );
};

export const renderDateRangeFilter = (
  filter: TFilterOption,
  values: TFilterValues,
  onChange: (key: string, value: TFilterValue) => void
): React.ReactNode => {
  if (filter.type !== "date-range") return <></>;
  const { title, key } = filter;
  const dateRangeValues = values[key] as IDateRangeValues;

  return (
    <FilterBodyItem css={filterBodyItemCss}>
      {title && (
        <div className="flex items-center h-10">
          <Paragraph4
            fontWeight="600"
            lineHeight="20px"
            textColor="#1A202C"
          >
            {title}
          </Paragraph4>
        </div>
      )}
      <div className="flex gap-4 items-center">
        <div className="inline-flex items-center gap-2 h-10 w-[152px]">
          <DateInput
            name="startDate"
            value={
              dateRangeValues.startDate
                ? new Date(dateRangeValues.startDate)
                : null
            }
            onChange={(value) => {
              if (!value) return;
              // Prevent selecting a startDate that is after endDate
              if (
                dateRangeValues.endDate &&
                value > new Date(dateRangeValues.endDate)
              ) {
                return;
              }
              onChange(key, {
                ...dateRangeValues,
                startDate: formatDateToYYYYMMDD(value),
              });
            }}
            placeholder="Start Date"
            preventOpenOnFocus
            disabled={filter?.isDisabled}
          />
        </div>
        <div>
          <ArrowRightIcon />
        </div>
        <div className="inline-flex items-center gap-2 h-10 w-[152px]">
          <DateInput
            name="endDate"
            value={
              dateRangeValues.endDate ? new Date(dateRangeValues.endDate) : null
            }
            onChange={(value) => {
              if (!value) return;
              // Prevent selecting an endDate that is before startDate
              if (
                dateRangeValues.startDate &&
                value < new Date(dateRangeValues.startDate)
              ) {
                return;
              }
              onChange(key, {
                ...dateRangeValues,
                endDate: formatDateToYYYYMMDD(value),
              });
            }}
            disabled={!dateRangeValues.startDate || filter?.isDisabled}
            placeholder="End Date"
            preventOpenOnFocus
          />
        </div>
      </div>
    </FilterBodyItem>
  );
};

export const renderSelectFilter = (
  filter: TFilterOption,
  values: TFilterValues,
  onChange: (key: string, value: TFilterValue) => void
): React.ReactNode => {
  if (filter.type !== "select") return <></>;
  const { title, options, key, placeholder } = filter;
  const selectValue = values[key] as string;
  const selectedOption = options.find((option) => option.value === selectValue);

  return (
    <FilterBodyItem css={filterBodyItemCss}>
      {title && (
        <div className="flex items-center h-10">
          <Paragraph4
            fontWeight="600"
            lineHeight="20px"
            textColor="#1A202C"
          >
            {title}
          </Paragraph4>
        </div>
      )}
      <div className="flex-wrap flex gap-4">
        <Select
          placeholder={placeholder}
          options={options}
          name={key}
          value={selectedOption || null}
          onChange={(selected) => {
            const selectedValue = selected as IFilterFormFieldOption;
            onChange(key, selectedValue.value);
          }}
          menuPortalTarget={document.body}
          menuPosition="fixed"
          styles={reactSelectStyles}
          menuPlacement="top"
          isSearchable
          isDisabled={filter?.isDisabled}
        />
      </div>
    </FilterBodyItem>
  );
};

const reactSelectStyles: StylesConfig = {
  container: (base) => ({
    ...base,
    width: "100%",
  }),
  control: (base, state) => ({
    ...base,
    minHeight: "36px",
    height: "36px",
    borderRadius: "5px",
    border: "1px solid #D0D5DD",
    boxShadow: state.isFocused ? "rgba(0, 103, 112, 1)" : "none",
    borderColor: state.isFocused ? "rgba(0, 103, 112, 1)" : base.borderColor,
    borderWidth: state.isFocused ? "1px" : base.borderWidth,
    "&:hover": {
      borderColor: state.isFocused ? "rgba(0, 103, 112, 1)" : base.borderColor,
    },
  }),
  placeholder: (base) => ({
    ...base,
    fontSize: "14px",
    lineHeight: "20px",
    fontWeight: 400,
    color: "#667085",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    paddingRight: 10,

    "> svg": {
      fill: "#4A5564",
      width: "16px",
      height: "16px",
    },
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  singleValue: (base) => ({
    ...base,
    fontSize: "14px",
    lineHeight: "20px",
    fontWeight: 400,
    color: "#667085",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

export const renderRadioFilters = (
  filter: TFilterOption,
  values: TFilterValues,
  onChange: (key: string, value: TFilterValue) => void
): React.ReactNode => {
  if (filter.type !== "radio") return <></>;
  const { title, options, key } = filter;
  const radioValue = values[key] as string;

  return (
    <FilterBodyItem css={filterBodyItemCss}>
      {title && (
        <div className="flex items-center h-10">
          <Paragraph4
            fontWeight="600"
            lineHeight="20px"
            textColor="#1A202C"
          >
            {title}
          </Paragraph4>
        </div>
      )}
      <div className="flex-wrap flex gap-4">
        {options.map(({ label, value }, idx) => (
          <div
            className="inline-flex items-center gap-2 h-10 pr-4"
            key={idx}
          >
            <Box
              css={radioWrapper}
              className="flex items-center gap-2"
              key={idx}
            >
              <label
                className={`flex items-center ${filter?.isDisabled ? "cursor-not-allowed opacity-40" : "cursor-pointer"} text-[16px] text-[#4a4a4a]`}
              >
                <input
                  type="radio"
                  name={key}
                  value={value}
                  onChange={() => {
                    if (filter?.isDisabled) return;
                    onChange(key, value);
                  }}
                  checked={radioValue === value}
                  disabled={filter?.isDisabled}
                />
                <Span
                  className="custom-radio-circle"
                  css={radioCircle}
                ></Span>
                <Paragraph3
                  fontWeight="500"
                  lineHeight="20px"
                  textColor="#344054"
                >
                  {label}
                </Paragraph3>
              </label>
            </Box>
          </div>
        ))}
      </div>
    </FilterBodyItem>
  );
};
